import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@edil/api-client';

export interface Loaded<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  reload(): Promise<void>;
  setData(update: (prev: T | undefined) => T | undefined): void;
}

/**
 * Tiny data loader (keeps the bundle small instead of a query library). Keeps the last good data
 * when a refresh fails, so a flaky connection does not blank the screen.
 */
export function useLoad<T>(load: (() => Promise<T>) | null, deps: unknown[]): Loaded<T> {
  const [data, setDataState] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    const fn = loadRef.current;
    if (!fn) return;
    const id = ++latest.current;
    try {
      const value = await fn();
      if (id === latest.current) {
        setDataState(value);
        setError(undefined);
      }
    } catch (e) {
      if (id === latest.current) setError(e instanceof ApiError ? e : new ApiError(0, 'INTERNAL', String(e)));
    } finally {
      if (id === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, error, loading, reload, setData: (u) => setDataState((p) => u(p)) };
}

export function useInterval(fn: () => void, ms: number | null): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms === null) return;
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

